import type { GettextInterface } from "../../../shared/I18n";
import { DatadogLogger } from "../../../shared/logging/DatadogLogger";
import type { ViewjobQuery } from "../__generated__/viewjob.gqlgenerated";
import type { Employer } from "../viewjobOneGraphModel";

export interface BrandedJobProctorData {
  isBrandedJobMigrationTestActive: boolean;
  isBrandedJobMigrationShadow: boolean;
}

/**
 * Builds the employer model from ViewJob GraphQL data
 *
 * @param viewjob - The GraphQL ViewJob data
 * @returns The employer model object
 */
export const buildEmployerModel = (
  i18n: GettextInterface,
  viewjob: NonNullable<ViewjobQuery["viewjob"]>,
  brandedJobProctorData?: BrandedJobProctorData,
): Employer => {
  const jobKey = viewjob.job?.key;
  const sourceEmployerName = viewjob.job?.sourceEmployerName ?? "";
  const employer = viewjob.job?.employer;
  const details = viewjob.details;
  const globalReviewCount = employer?.ugcStats?.globalReviewCount ?? 0;
  const brandingEligibility = viewjob.job?.brandingEligibility ?? null;
  const localizedAssets = brandingEligibility?.brandingInformation?.localizedAssets ?? null;
  let logoUrl: string | null | undefined;
  let headerImageUrl: string | undefined;
  let isBrandedJob = false;

  const ratings = {
    count: globalReviewCount,
    rating: employer?.ugcStats?.ratings?.overallRating?.value ?? 0,
    countContent: i18n.ngettext(
      "{0} review",
      "{0} reviews",
      globalReviewCount,
      globalReviewCount.toLocaleString(),
    ),
  };

  const parentCompanyName =
    !employer?.subsidiaryOptOutStatusV2 && !employer?.parentEmployer?.subsidiaryOptOutStatusV2
      ? (employer?.parentEmployer?.name ?? undefined)
      : undefined;

  const compareShadowMetrics = (
    eligibility: NonNullable<typeof brandingEligibility>,
    legacyLogo: string | null | undefined,
    legacyHeader: string | undefined,
    legacyIsBrandedJob: boolean,
  ) => {
    const assets = eligibility.brandingInformation?.localizedAssets;
    const apiShouldShowBranding = !!eligibility.shouldShowBranding;
    const apiLogoUrl = assets?.logo?.url ?? undefined;
    const apiBannerUrl = assets?.banner?.url ?? undefined;

    const eligibilityMismatch = legacyIsBrandedJob !== apiShouldShowBranding;

    const shouldCompareAssets = !eligibilityMismatch && apiShouldShowBranding;

    const logoMismatch = shouldCompareAssets ? legacyLogo !== apiLogoUrl : false;
    const headerMismatch = shouldCompareAssets ? legacyHeader !== apiBannerUrl : false;

    const context: Record<string, unknown> = {
      surface: "rnvj",
      compared: true,
      matched: !eligibilityMismatch && !logoMismatch && !headerMismatch,
      eligibilityMismatch,
      logoMismatch,
      headerMismatch,
      reasonMismatch: false, // ViewJob does not have branding reasons
      missingResult: false,
      jobKey,
    };

    if (eligibilityMismatch) {
      context.legacyIsBrandedJob = legacyIsBrandedJob;
      context.apiShouldShowBranding = apiShouldShowBranding;
    }

    if (logoMismatch) {
      context.legacyLogoUrl = legacyLogo ?? null;
      context.apiLogoUrl = apiLogoUrl ?? null;
    }

    if (headerMismatch) {
      context.legacyHeaderUrl = legacyHeader ?? null;
      context.apiBannerUrl = apiBannerUrl ?? null;
    }

    void DatadogLogger.info("branding.shadow.comparison", context);
  };

  const isBrandedJobMigrationTestActive =
    brandedJobProctorData?.isBrandedJobMigrationTestActive ?? false;
  // If branded job migration test is active, use API branding eligibility for branding checks and assets
  if (isBrandedJobMigrationTestActive) {
    if (brandingEligibility != null) {
      logoUrl = localizedAssets?.logo?.url ?? undefined;
      headerImageUrl = localizedAssets?.banner?.url ?? undefined;
      isBrandedJob = brandingEligibility.shouldShowBranding ?? false;
    }
  } else {
    // If branded job migration test is not active, use legacy branding checks and assets
    logoUrl =
      employer?.dossier?.images?.squareLogoUrls?.url256 ??
      employer?.dossier?.images?.rectangularLogoUrl;
    headerImageUrl = employer?.dossier?.images?.headerImageUrls?.url1960x400;
    isBrandedJob = details?.isBrandedJob ?? false;
  }

  const isBrandedJobMigrationShadow = brandedJobProctorData?.isBrandedJobMigrationShadow ?? false;

  // If branded job migration shadow is active, compare API branding eligibility with legacy branding eligibility
  if (isBrandedJobMigrationShadow) {
    if (brandingEligibility == null) {
      if (details?.isBrandedJob != null) {
        void DatadogLogger.info("branding.shadow.comparison", {
          surface: "rnvj",
          compared: true,
          missingResult: true,
          jobKey,
        });
      }
    } else {
      compareShadowMetrics(brandingEligibility, logoUrl, headerImageUrl, isBrandedJob);
    }
  }

  const hasEnhancedJobDescription = !!logoUrl && !!headerImageUrl && isBrandedJob;

  return {
    key: employer?.key ?? "",
    companyName: sourceEmployerName,
    companyOverviewLink: details?.companyLinks?.companyOverviewLink ?? undefined,
    companyReviewLink: details?.companyLinks?.companyReviewLink ?? undefined,
    ratings,
    parentCompanyName,
    hideRating: false,
    companyImagesModel: {
      logoUrl: logoUrl ?? undefined,
      headerImageUrl: headerImageUrl ?? undefined,
      logoImageOverlayLower: hasEnhancedJobDescription,
      logoAltText: sourceEmployerName ? `${sourceEmployerName} logo` : "",
      showBannerTop: hasEnhancedJobDescription,
      showIconInTitle: false,
    },
  };
};
